import { Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, UpdateBusinessDetailsInput } from '../../../api';
import { FormTextField } from '../../../experienceSettings/formComponents/FormTextField';

type ContactTabProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

export const ContactTab = ({ account, updateLocalAccount, setIsUpdatedSnackOpen }: ContactTabProps) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);

  const initialValues = {
    email: account?.business_details?.contact?.email || '',
    phone: account?.business_details?.contact?.phone || '',
    instagram: account?.business_details?.contact?.instagram || '',
    website: account?.business_details?.contact?.website || '',
  };

  const validationSchema = yup.object().shape({
    email: yup.string().email(),
    website: yup.string().url(),
  });

  const onSubmit = async (values: typeof initialValues) => {
    setIsUpdating(true);
    const input: UpdateBusinessDetailsInput = {
      contact: {
        email: values.email || undefined,
        phone: values.phone || undefined,
        instagram: values.instagram || undefined,
        website: values.website || undefined,
      },
    };
    const updatedAccount = await AccountService.updateBusinessDetails(input);
    await updateLocalAccount(updatedAccount);
    setIsUpdating(false);
    setIsUpdatedSnackOpen(true);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container flexDirection="column">
            <Grid container flexDirection="column" flex={1} gap={2}>
              <Grid item>
                <Typography variant="h4">{t('business_details.contact')}</Typography>
              </Grid>
              <Grid item>
                <Typography variant="body1" color="text.secondary">
                  {t('business_details.note')}
                </Typography>
              </Grid>
              <Grid item>
                <FormTextField
                  label={t('business_details.email')}
                  fieldName="email"
                  type="email"
                  isFieldUpdate
                  disabled={isUpdating}
                />
              </Grid>
              <Grid item>
                <FormTextField
                  label={t('business_details.phone')}
                  fieldName="phone"
                  isFieldUpdate
                  disabled={isUpdating}
                />
              </Grid>
              <Grid item>
                <FormTextField
                  label={t('business_details.instagram')}
                  fieldName="instagram"
                  placeholder="@username"
                  isFieldUpdate
                  disabled={isUpdating}
                />
              </Grid>
              <Grid item>
                <FormTextField
                  label={t('business_details.website')}
                  fieldName="website"
                  placeholder="https://"
                  isFieldUpdate
                  disabled={isUpdating}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" mt={3}>
              <Button type="submit" variant="contained" disabled={isUpdating}>
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
